import React, { ReactElement, ReactNode } from 'react';

import { CustomPropTypes } from '@philips-emr/tws-core/lib/component';
import DataContext from '@philips-emr/tws-core/lib/component/context/data-context';
import { bool, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { noop } from '@philips-emr/tws-utils';

import EngineContext from '../context/engine-context';
import FragmentNode from '../core/fragment-node';
import Engine, { FragmentProviderOptions } from '../engine';

const { withInfo } = CustomPropTypes.Util;

export const fragmentName = withInfo(noop, { type: 'fragment' });

export interface BaseFragmentProps {
  disposable: boolean;
  fragment?: string;
  instanceUUID: string;
  uuid: string;
}

export interface BaseFragmentState {
  content: ReactElement | null;
  contextData: Record<string, any>;
  loaded: boolean;
  node: FragmentNode | null;
}

abstract class BaseFragment<Props extends BaseFragmentProps, State extends BaseFragmentState> extends React.Component<Props, State> {

  static contextType = EngineContext;

  static propTypes = {
    disposable: bool,
    fragment: fragmentName,
    instanceUUID: string.notEnumerable(),
    uuid: string,
  };

  static defaultProps = {
    disposable: true,
  };

  context!: React.ContextType<typeof EngineContext>;

  unlisten: Function = noop;

  componentDidMount(): void {
    this.loadFragment();
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.props.fragment !== prevProps.fragment) {

      if (prevProps.fragment) {
        this.context.engine.discardFragment(this.props.instanceUUID);
      }

      this.loadFragment(true);
    }
  }

  componentWillUnmount(): void {
    if (this.props.disposable) {
      this.unlisten();
      this.discardFragmentIntance();
    }
  }

  updateContextData = (data: Record<string, any>): void => {
    this.setState({ contextData: data });
  };

  discardFragmentIntance(): void {
    this.context.engine.discardFragment(this.props.instanceUUID);
  }

  loadFragment(forceLoad = false): void {
    const { content } = this.state;

    if (!content || forceLoad) {
      const { fragment, uuid } = this.props;

      if (!fragment) {
        return this.setState({
          content: null,
          contextData: {},
          loaded: false,
          node: null,
        });
      }

      const { engine, parent } = this.context;

      // @ts-ignore
      const node = this.provideFragment(engine, { fragment, instanceUUID: uuid, parent });
      const fragmentContent = this.renderFragment(node);

      const context = node.getDataContext();

      this.unlisten();
      this.unlisten = context.addListener(this.updateContextData);

      this.setState({ loaded: true, node, content: fragmentContent, contextData: context.getBulkData() });

    }
  }

  abstract provideFragment(engine: Engine, options: FragmentProviderOptions): FragmentNode;

  renderFragment(fragment: FragmentNode): ReactElement {
    const root = fragment.getMetadata().getRoot();
    return this.context.engine.render(root) as ReactElement;
  }

  render(): ReactNode {
    const { content } = this.state;
    const { contextData } = this.state;

    const engineParameters = {
      engine: this.context.engine,
      parent: this.state.node,
    };

    return (
      <EngineContext.Provider value={engineParameters}>
        <DataContext.Context.Provider value={contextData}>
          {content}
        </DataContext.Context.Provider>
      </EngineContext.Provider>
    );
  }
}

export default BaseFragment;
